import HeroLogo from "@/components/HeroLogo";
import { Link } from "react-router-dom";
import { ArrowRight, Plane, Compass, Sparkles, ShieldCheck, Globe2, Star, Mail, MapPin, Building2, Hash } from "lucide-react";
import { BangladeshFlag, SwedenFlag } from "@/components/Flags";

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="bg-stage" />
        <div className="absolute inset-0 pointer-events-none">
          <div className="bg-grid" />
          <div className="orb orb-green" />
          <div className="orb orb-red" />
        </div>
        <div className="relative z-10 max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-20 sm:py-28 lg:py-32 grid lg:grid-cols-[1.1fr_0.9fr] gap-12 items-center">
          <div>
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.03] text-[11px] tracking-[0.3em] uppercase"
              data-testid="hero-pill"
            >
              <Sparkles className="w-3.5 h-3.5 text-[color:var(--bd-green-bright)]" />
              Bangladesh · Sweden · Worldwide
            </div>
            <h1 className="font-display mt-6 text-5xl sm:text-6xl lg:text-7xl xl:text-[88px] leading-[0.95]">
              Journeys made with <span className="text-[color:var(--bd-green-bright)]">heart</span>.
              <br />
              Flights handled with <span className="text-[color:var(--bd-red-bright)]">care</span>.
            </h1>
            <p className="mt-6 max-w-xl text-base sm:text-lg text-[color:var(--ivory-muted)] leading-relaxed">
              From the rivers of Bangladesh to the streets of Stockholm — Bangladesh Travels &amp; Tours AB
              handles every flight and visa with attention you can feel.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link to="/contact" className="pill pill-primary inline-flex items-center gap-2" data-testid="hero-cta-primary">
                Plan your trip <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/flights"
                className="pill border border-white/15 text-white hover:bg-white/[0.04]"
                data-testid="hero-cta-flights"
              >
                Search flights
              </Link>
            </div>
            <div className="mt-10 flex items-center gap-5 text-sm text-[color:var(--ivory-muted)]">
              <div className="flex items-center gap-2">
                <BangladeshFlag className="w-7 h-5 rounded-sm" />
                Dhaka
              </div>
              <Plane className="w-4 h-4" />
              <div className="flex items-center gap-2">
                <SwedenFlag className="w-7 h-5 rounded-sm" />
                Stockholm
              </div>
            </div>
          </div>

          {/* Hero visual */}
          <div className="relative hidden lg:block">
            <HeroLogo />
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="py-16 sm:py-24 border-y border-white/5">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Plane, title: "Smart Flights", text: "Negotiated fares to Dhaka, Sylhet, Chittagong & 200+ cities — economy to business." },
              { icon: ShieldCheck, title: "Visas & Permits", text: "End-to-end visa support for Bangladesh, Schengen, UK and more — paperwork done right." },
              { icon: Globe2, title: "Group & Corporate", text: "Family, religious, and corporate group travel — managed from quote to return." },
              { icon: Compass, title: "Travel Advisory", text: "Honest advice on routes, timing, baggage rules and the best fare windows." },
            ].map((p, i) => {
              const Icon = p.icon;
              return (
                <div
                  key={i}
                  className="p-6 rounded-2xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] transition"
                  data-testid={`pillar-${i}`}
                >
                  <div className="w-10 h-10 rounded-xl bg-[color:var(--bd-green)]/15 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-[color:var(--bd-green-bright)]" />
                  </div>
                  <div className="font-display text-xl mb-2">{p.title}</div>
                  <p className="text-sm text-[color:var(--ivory-muted)] leading-relaxed">{p.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Company Information */}
      <section className="py-16 sm:py-24" data-testid="company-info-section">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="text-center mb-12">
            <div className="text-[11px] tracking-[0.3em] uppercase text-[color:var(--bd-green-bright)] mb-3">About the company</div>
            <h2 className="font-display text-4xl sm:text-5xl">A registered Swedish travel agency</h2>
            <p className="mt-4 text-[color:var(--ivory-muted)] max-w-2xl mx-auto">
              You're booking with a real, registered business — based in Stockholm, operating between Sweden and Bangladesh.
            </p>
          </div>

          <div className="relative rounded-3xl overflow-hidden border border-[color:var(--bd-green)]/30">
            <div className="absolute inset-0 bg-gradient-to-br from-[color:var(--bd-green)]/15 via-transparent to-[color:var(--bd-red)]/10 pointer-events-none" />
            <div className="absolute top-0 left-0 right-0 h-1 flex">
              <div className="flex-[2.2] bg-gradient-to-r from-[#006A4E] via-[#00A36C] to-[#006A4E]" />
              <div className="flex-1 bg-gradient-to-r from-[#F42A41] to-[#DA291C]" />
            </div>

            <div className="relative grid md:grid-cols-2 gap-0 p-6 sm:p-10">
              <div className="md:pr-10 md:border-r border-white/10">
                <div className="flex items-center gap-3 mb-6">
                  <span className="w-12 h-12 rounded-full bg-white flex items-center justify-center overflow-hidden">
                    <img src="https://customer-assets.emergentagent.com/job_c9f69bc0-91ae-40a1-9da9-440da68b284c/artifacts/xyedl7ct_IMG_8766.png" alt="BDTT" className="w-[88%] h-[88%] object-contain logo-img" />
                  </span>
                  <div>
                    <div className="font-display text-2xl leading-tight">Bangladesh Travels &amp; Tours AB</div>
                    <div className="text-xs tracking-[0.25em] uppercase text-[color:var(--ivory-muted)] mt-1">Sweden &middot; Bangladesh</div>
                  </div>
                </div>

                <ul className="space-y-5">
                  <li className="flex items-start gap-3">
                    <Building2 className="w-4 h-4 text-[color:var(--bd-green-bright)] mt-1 shrink-0" />
                    <div>
                      <div className="text-[11px] tracking-[0.25em] uppercase text-[color:var(--ivory-muted)] mb-0.5">Company name</div>
                      <div className="text-white">Bangladesh Travels &amp; Tours AB</div>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Hash className="w-4 h-4 text-[color:var(--bd-green-bright)] mt-1 shrink-0" />
                    <div>
                      <div className="text-[11px] tracking-[0.25em] uppercase text-[color:var(--ivory-muted)] mb-0.5">Organisation number</div>
                      <div className="text-white font-mono">559562-4361</div>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <MapPin className="w-4 h-4 text-[color:var(--bd-green-bright)] mt-1 shrink-0" />
                    <div>
                      <div className="text-[11px] tracking-[0.25em] uppercase text-[color:var(--ivory-muted)] mb-0.5">Registered address</div>
                      <div className="text-white leading-relaxed">
                        Kråksätrabacken 27<br />
                        127 30 Skärholmen<br />
                        Sweden
                      </div>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Mail className="w-4 h-4 text-[color:var(--bd-green-bright)] mt-1 shrink-0" />
                    <div>
                      <div className="text-[11px] tracking-[0.25em] uppercase text-[color:var(--ivory-muted)] mb-0.5">Email</div>
                      <a href="mailto:info@bdtt.se" className="text-white hover:text-[color:var(--bd-green-bright)]">info@bdtt.se</a>
                    </div>
                  </li>
                </ul>
              </div>

              <div className="md:pl-10 mt-8 md:mt-0 flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <BangladeshFlag className="w-9 h-6 rounded-sm" />
                    <SwedenFlag className="w-9 h-6 rounded-sm" />
                  </div>
                  <p className="text-[color:var(--ivory-muted)] leading-relaxed">
                    A Swedish-registered travel agency with deep roots in Bangladesh. We help you fly safely between Stockholm
                    and Dhaka — and to 200+ cities worldwide — with friendly, expert support every step of the way.
                  </p>
                </div>

                <div className="mt-8 flex flex-wrap gap-3">
                  <Link to="/contact" className="pill pill-primary inline-flex items-center gap-2" data-testid="company-cta-contact">
                    Get in touch <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link to="/flights" className="pill border border-white/15 text-white hover:bg-white/[0.04]" data-testid="company-cta-flights">
                    Search flights
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-24 border-y border-white/5 bg-[#0A0D11]">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
          <h2 className="font-display text-4xl sm:text-5xl mb-12">Travellers who came back smiling</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { name: "Sara Karlsson", role: "Stockholm → Dhaka", quote: "BDTT handled my visa paperwork and rebooked my return when my plans changed. Absolute professionals." },
              { name: "Tanvir Rahman", role: "Family booking · 6 pax", quote: "BDTT booked our entire family from Stockholm to Dhaka and arranged every connection. Every flight was on time. Highly recommended." },
              { name: "Anna Lindberg", role: "Schengen visa for parents", quote: "They walked my mom through every form. She actually said the process felt easy." },
            ].map((t, i) => (
              <div key={i} className="p-6 rounded-2xl border border-white/10 bg-white/[0.02]" data-testid={`testimonial-${i}`}>
                <div className="flex gap-1 text-[color:var(--bd-green-bright)] mb-4">
                  {[0,1,2,3,4].map(j => <Star key={j} className="w-4 h-4 fill-current" />)}
                </div>
                <p className="text-[color:var(--ivory)] mb-5 leading-relaxed">"{t.quote}"</p>
                <div className="text-sm">
                  <div className="font-semibold">{t.name}</div>
                  <div className="text-[color:var(--ivory-muted)]">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 sm:py-28">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 lg:px-12 text-center">
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl">
            Tell us your dream trip.
            <br />
            We'll build it.
          </h2>
          <p className="mt-5 text-[color:var(--ivory-muted)] max-w-2xl mx-auto">
            Whether it's a family booking or a one-way ticket to start a new chapter — we plan it,
            book it, and stay with you all the way.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="pill pill-primary inline-flex items-center gap-2">
              Get a quote <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="mailto:info@bdtt.se" className="pill border border-white/15 text-white hover:bg-white/[0.04]">
              info@bdtt.se
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
